/**
 * FilterValueAutocompleteCustom
 * Custom autocomplete built with useAutocomplete hook for better control
 * Supports server-side filtering, loading states, lazy loading, and optional counts
 */

import type { SyntheticEvent, UIEvent, MouseEvent } from 'react';
import { useRef, useEffect, type KeyboardEvent } from 'react';

import {
  Box,
  Checkbox,
  Radio,
  Skeleton,
  Stack,
  Typography,
  useAutocomplete,
} from '@mui/material';
import type { UseAutocompleteProps } from '@mui/material';
import cx from 'classnames';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import styles from './FilterValueAutocomplete.module.scss';
import {
  FacetOptionHoverCardLayer,
  useHoverCardStore,
} from '@/components/filters/FacetOptionHoverCard';
import type { FacetOption } from '@/hooks/filters/types';
import type { FacetConfig } from '@/lib/resource-config/types';

/**
 * Sanitize a slug for use in data-automation-id attributes
 * Replaces spaces and special characters with hyphens and converts to lowercase
 * Handles both string and numeric slugs
 */
const sanitizeSlugForAutomationId = (slug: string | number): string => {
  const slugStr = String(slug);
  return slugStr
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric chars with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

interface FilterValueAutocompleteCustomProps {
  /**
   * Available options for the autocomplete
   */
  options: FacetOption[];

  /**
   * Currently selected values
   */
  value: FacetOption[];

  /**
   * Current search input value
   */
  inputValue?: string;

  /**
   * Callback when selected values change
   */
  onChange: (event: SyntheticEvent, newValue: FacetOption[]) => void;

  /**
   * Callback when input value changes
   */
  onInputChange?: (
    event: SyntheticEvent,
    value: string,
    reason: string,
  ) => void;

  /**
   * Callback when user scrolls near the bottom of the listbox (for lazy loading)
   */
  onLoadMore?: () => void;

  /**
   * Whether there are more items to load
   */
  hasMore?: boolean;

  /**
   * Label for the text field
   */
  label?: string;

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Whether data is currently loading
   */
  loading?: boolean;

  /**
   * Whether to show counts next to options
   */
  showCounts?: boolean;

  /**
   * Auto-focus the input on mount
   */
  autoFocus?: boolean;

  /**
   * Callback when user presses Escape key
   * If provided, parent can handle escape behavior
   */
  onEscape?: () => void;

  /**
   * Callback when user presses Left Arrow at the beginning of input
   * Useful for hierarchical menus (e.g., going back to parent menu)
   */
  onLeftArrow?: () => void;

  /**
   * Callback when user clicks a label (should close the popover)
   */
  onCloseAfterSelection?: () => void;

  /**
   * Single selection mode (radio buttons) vs multiple selection (checkboxes)
   * Default: false (multiple selection with checkboxes)
   */
  singleSelect?: boolean;

  /**
   * Optional facet configuration for hover cards
   */
  facetConfig?: FacetConfig;
}

export const FilterValueAutocomplete = ({
  options,
  value,
  inputValue = '',
  onChange,
  onInputChange,
  onLoadMore,
  hasMore = false,
  placeholder = 'Select values...',
  loading = false,
  showCounts = false,
  autoFocus = false,
  onEscape,
  onLeftArrow,
  onCloseAfterSelection,
  singleSelect = false,
  facetConfig,
}: FilterValueAutocompleteCustomProps) => {
  const { t } = useTranslation('views');
  const listboxRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastHighlightedOptionRef = useRef<string | null>(null);

  const hoverIntentTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hoverCardEnabled = Boolean(
    facetConfig?.hoverCard && facetConfig?.valueSource?.kind === 'collection',
  );
  const hoverDelay = facetConfig?.hoverCard?.delayMs ?? 250;

  // Hover card state lives in a global store so updates never re-render this component.
  // That keeps MUI's useAutocomplete focus state stable (no jump-to-first-item on hover/keyboard).
  const getCardOpen = () => Boolean(useHoverCardStore.getState().anchorEl);

  const openCardForOption = (optionId: string) => {
    if (!hoverCardEnabled) return;
    const focused =
      listboxRef.current?.querySelector<HTMLElement>('.Mui-focused') || null;
    useHoverCardStore.getState().setCard(focused, optionId);
  };

  // Determine if this is initial loading (no options yet) vs lazy loading (has options)
  const isInitialLoading = loading && options.length === 0;
  const isLazyLoading = loading && options.length > 0;

  // Create loading skeleton placeholder
  const loadingPlaceholder: FacetOption = {
    id: '__loading__',
    slug: '__loading__',
    label: '__loading__',
  };

  // Organize options: selected values at top, then unselected options
  // This allows users to see and uncheck selected items while preventing duplicates
  const organizedOptions = (() => {
    if (singleSelect) {
      // Single select: show all options in original order
      return options;
    }

    // Multi-select: put selected options first, then unselected options
    const selectedIds = new Set(value.map((v) => v.id));
    const unselectedOptions = options.filter((opt) => !selectedIds.has(opt.id));

    // Selected values with proper labels (from options if available)
    const selectedOptionsWithLabels = value.map((selected) => {
      const matchingOption = options.find((opt) => opt.id === selected.id);
      return matchingOption || selected;
    });

    return [...selectedOptionsWithLabels, ...unselectedOptions];
  })();

  // Append loading indicator to options if we're loading
  // - Initial loading: Show skeleton even if we only have selected values (prevents showing only selected items)
  // - Lazy loading: Show skeleton for infinite scroll
  const optionsWithLoader =
    isInitialLoading || (isLazyLoading && hasMore)
      ? [...organizedOptions, loadingPlaceholder]
      : organizedOptions;

  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
  } = useAutocomplete({
    multiple: !singleSelect,
    open: true, // Always keep the listbox open
    options: optionsWithLoader,
    value: singleSelect ? (value[0] ?? null) : value,
    inputValue,
    disableListWrap: true,
    onChange: (event, newValue) => {
      // For single select, wrap the value in an array to maintain consistent API
      const normalizedValue = singleSelect
        ? newValue
          ? [newValue as FacetOption]
          : []
        : (newValue as FacetOption[]);
      onChange(event as SyntheticEvent, normalizedValue);
    },
    onInputChange: (event, newValue, reason) => {
      if (onInputChange) {
        onInputChange(event as SyntheticEvent, newValue, reason);
      }
    },
    getOptionLabel: (option) => (option as FacetOption).label,
    isOptionEqualToValue: (option, value) =>
      (option as FacetOption).id === (value as FacetOption).id,
    filterOptions: (x) => x, // Server-side filtering
    getOptionDisabled: (option) =>
      (option as FacetOption).slug === '__loading__',
    onHighlightChange: (_event, option, reason) => {
      if (!hoverCardEnabled) return;
      if (reason === 'keyboard') {
        const optionId = (option as FacetOption | null)?.id ?? null;
        lastHighlightedOptionRef.current = optionId;
        if (getCardOpen()) {
          if (optionId) {
            openCardForOption(optionId);
          } else {
            useHoverCardStore.getState().clear();
          }
        }
      }
    },
  } as UseAutocompleteProps<FacetOption, boolean, false, false>);

  /**
   * Handle scroll events in the listbox for lazy loading
   */
  const handleListboxScroll = (event: UIEvent<HTMLUListElement>) => {
    const listboxNode = event.currentTarget;
    const scrollTop = listboxNode.scrollTop;
    const scrollHeight = listboxNode.scrollHeight;
    const clientHeight = listboxNode.clientHeight;

    // Load more when user scrolls within 100px of the bottom
    const threshold = 100;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom < threshold && hasMore && !loading && onLoadMore) {
      onLoadMore();
    }
  };

  // Auto-focus input on mount
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleMouseEnter = (
    event: MouseEvent<HTMLLIElement>,
    optionId: string,
  ) => {
    if (!hoverCardEnabled) return;
    if (hoverIntentTimeout.current) {
      clearTimeout(hoverIntentTimeout.current);
    }
    const anchor = event.currentTarget;
    hoverIntentTimeout.current = setTimeout(() => {
      lastHighlightedOptionRef.current = optionId;
      useHoverCardStore.getState().setCard(anchor, optionId);
    }, hoverDelay);
  };

  const handleMouseLeave = () => {
    if (!hoverCardEnabled) return;
    if (hoverIntentTimeout.current) {
      clearTimeout(hoverIntentTimeout.current);
      hoverIntentTimeout.current = null;
    }
    useHoverCardStore.getState().scheduleClear();
  };
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      if (hoverIntentTimeout.current) {
        clearTimeout(hoverIntentTimeout.current);
      }
      if (listboxRef.current?.contains(useHoverCardStore.getState().anchorEl)) {
        useHoverCardStore.getState().clear();
      }
    };
  }, []);

  /**
   * Handle keyboard navigation before MUI's autocomplete
   * Allows parent to intercept specific keys for custom behavior
   */
  const inputPropsFromHook = getInputProps();

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    const cursorAtStart = target.selectionStart === 0;
    const inputEmpty = inputValue.trim() === '';

    switch (e.key) {
      case 'Escape':
        useHoverCardStore.getState().clear();
        if (onEscape) {
          e.preventDefault();
          e.stopPropagation();
          onEscape();
          return;
        }
        break;

      case 'ArrowLeft': {
        if (getCardOpen()) {
          e.preventDefault();
          e.stopPropagation();
          useHoverCardStore.getState().clear();
          return;
        }
        if ((cursorAtStart || inputEmpty) && onLeftArrow) {
          e.preventDefault();
          e.stopPropagation();
          onLeftArrow();
          return;
        }
        break;
      }

      case 'ArrowRight': {
        if (!hoverCardEnabled) break;
        let highlightedId = lastHighlightedOptionRef.current;
        let anchor: HTMLElement | null =
          listboxRef.current?.querySelector<HTMLElement>('.Mui-focused') ??
          null;

        if (!highlightedId) {
          const firstItem =
            listboxRef.current?.querySelector<HTMLElement>('[data-option-id]');
          if (firstItem?.dataset.optionId) {
            highlightedId = firstItem.dataset.optionId;
            anchor = firstItem;
          }
        }

        if (highlightedId) {
          e.preventDefault();
          e.stopPropagation();
          useHoverCardStore.getState().setCard(anchor, highlightedId);
          return;
        }
        break;
      }

      case 'Enter':
        // Enter key behavior depends on modifier keys
        // Only when navigating the list (not typing in input)
        if (inputEmpty) {
          // Let MUI handle the selection first
          inputPropsFromHook.onKeyDown?.(e);

          // Ctrl+Enter or Cmd+Enter: close popover after selection
          if (e.ctrlKey || e.metaKey) {
            if (onCloseAfterSelection) {
              // Clear any existing timeout before setting a new one
              if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
              }
              closeTimeoutRef.current = setTimeout(() => {
                onCloseAfterSelection();
                closeTimeoutRef.current = null;
              }, 0);
            }
          }
          // Plain Enter: keep popover open (no additional action needed)
          return;
        }
        break;
    }

    // Let MUI's autocomplete handle the event if not intercepted
    inputPropsFromHook.onKeyDown?.(e);
  };

  const mergedInputRef = (element: HTMLInputElement | null) => {
    inputRef.current = element;
    if (typeof inputPropsFromHook.ref === 'function') {
      inputPropsFromHook.ref(element);
    } else if (inputPropsFromHook.ref && 'current' in inputPropsFromHook.ref) {
      inputPropsFromHook.ref.current = element;
    }
  };

  const listboxPropsFromHook = getListboxProps();

  // MUI doesn't type the ref on listbox props, so we merge it manually
  const mergedListboxRef = (element: HTMLUListElement | null) => {
    listboxRef.current = element;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const muiRef = (listboxPropsFromHook as any).ref;
    if (typeof muiRef === 'function') {
      muiRef(element);
    } else if (muiRef && 'current' in muiRef) {
      muiRef.current = element;
    }
  };

  return (
    <Stack className={styles.Container}>
      <Box {...getRootProps()}>
        <label
          {...getInputLabelProps()}
          className={cx(styles.Label, getInputLabelProps().className)}
        >
          {placeholder}
        </label>
        <input
          {...inputPropsFromHook}
          ref={mergedInputRef}
          placeholder={placeholder}
          className={cx(styles.Input, inputPropsFromHook.className)}
          onKeyDown={handleKeyDown}
          data-automation-id="filter-value-autocomplete-input"
        />
      </Box>

      {(groupedOptions as FacetOption[]).length > 0 && (
        <Stack
          {...listboxPropsFromHook}
          component="ul"
          gap={0.5}
          ref={mergedListboxRef}
          onScroll={onLoadMore ? handleListboxScroll : undefined}
          className={styles.Listbox}
        >
          {(groupedOptions as FacetOption[]).map((option, index) => {
            const { key, ...optionProps } = getOptionProps({ option, index });
            const isSelected = value.some((v) => v.id === option.id);

            // Use option.id as key to ensure uniqueness (handles duplicate names)
            const uniqueKey = option.id || key;

            // Render loading skeleton for the loading placeholder
            if (option.slug === '__loading__') {
              return (
                <li
                  style={{ cursor: 'default' }}
                  key={uniqueKey}
                  {...optionProps}
                >
                  <Box className={styles.LoadingSkeleton}>
                    <Skeleton
                      variant="rectangular"
                      width={18}
                      height={18}
                      className={styles.SkeletonIcon}
                    />
                    <Skeleton variant="text" width="60%" height={20} />
                    {showCounts && (
                      <Skeleton
                        variant="text"
                        width={30}
                        height={20}
                        className={styles.SkeletonCount}
                      />
                    )}
                  </Box>
                </li>
              );
            }

            const sanitizedSlug = sanitizeSlugForAutomationId(option.slug);

            return (
              <li
                key={uniqueKey}
                {...optionProps}
                data-option-id={option.id}
                onMouseEnter={(e) => handleMouseEnter(e, option.id)}
                onMouseLeave={handleMouseLeave}
                className={cx(optionProps.className, styles.Item)}
                data-automation-id={`filter-value-option-${sanitizedSlug}`}
              >
                {singleSelect ? (
                  <Radio
                    checked={isSelected}
                    className={styles.SelectionControl}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                    onClick={() => {
                      // Radio click: select value and close popover
                      // Let the event bubble to the li element to trigger MUI's selection
                    }}
                    data-automation-id={`filter-value-option-radio-${sanitizedSlug}`}
                  />
                ) : (
                  <Checkbox
                    checked={isSelected}
                    className={styles.SelectionControl}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                    onClick={() => {
                      // Checkbox click: toggle selection but keep popover open
                      // Let the event bubble to the li element to trigger MUI's selection
                    }}
                    data-automation-id={`filter-value-option-checkbox-${sanitizedSlug}`}
                  />
                )}
                <Box
                  className={styles.OptionLabel}
                  onClick={() => {
                    // Label click: toggle selection AND close popover
                    // Don't stop propagation - let MUI handle the selection
                    // Then close after a short delay to ensure selection completes
                    if (onCloseAfterSelection) {
                      // Clear any existing timeout before setting a new one
                      if (closeTimeoutRef.current) {
                        clearTimeout(closeTimeoutRef.current);
                      }
                      closeTimeoutRef.current = setTimeout(() => {
                        onCloseAfterSelection();
                        closeTimeoutRef.current = null;
                      }, 0);
                    }
                  }}
                  data-automation-id={`filter-value-option-label-${sanitizedSlug}`}
                >
                  {option.highlightedLabel ? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: option.highlightedLabel,
                      }}
                    />
                  ) : (
                    option.label
                  )}
                </Box>
                {showCounts && option.count !== undefined && (
                  <Typography variant="label" color="text.secondary">
                    {option.count}
                  </Typography>
                )}
              </li>
            );
          })}
        </Stack>
      )}

      {(groupedOptions as FacetOption[]).length === 0 && (
        <Box className={styles.EmptyState}>
          <Typography>{t('NoOptions')}</Typography>
        </Box>
      )}

      {/* Hover card - rendered outside the autocomplete tree to avoid rerenders affecting focus */}
      {hoverCardEnabled &&
        createPortal(
          <FacetOptionHoverCardLayer facetConfig={facetConfig!} />,
          document.body,
        )}
    </Stack>
  );
};
